// Экран 0 — «Диагностика». Первые 12–15 вопросов.
//
// Своей оценки ученик не видит: ни вероятностей, ни «ты знаешь на 0,42». Показ
// оценки превращает диагностику в экзамен, а она — первые пятнадцать шагов
// непрерывного процесса. Наверху только счётчик вопросов.
//
// «Не знаю» — не кнопка вежливости, а самый сильный сигнал в модели: по данным
// ALEKS он экономит вопросов больше, чем любая оптимизация выбора. Поэтому она
// равноправна с «Проверить», а не спрятана мелким текстом.

import { useEffect, useRef, useState } from "react";
import { api, useMutation, useQuery, type DiagnosticAnswerResult } from "../api";
import { Formula } from "../lib/Formula";
import { Solution } from "../ui/Solution";

export function Diagnostic({
  learnerKey,
  onFinished,
}: {
  learnerKey: string;
  onFinished: () => void;
}) {
  const state = useQuery(api.diagnostic.current, { learnerKey });
  const start = useMutation(api.diagnostic.start);
  const answer = useMutation(api.diagnostic.answer);

  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [unparsable, setUnparsable] = useState(false);
  const [last, setLast] = useState<DiagnosticAnswerResult | null>(null);
  // Своё состояние, а не `state.status`: запрос переключается на «done» в ту
  // же секунду, и панель «Карта построена» промелькнула бы. Уходит с экрана
  // ученик кнопкой, а не подписка.
  const [finished, setFinished] = useState(false);
  const starting = useRef(false);

  const question = state?.status === "running" ? state.question : null;

  useEffect(() => {
    if (state === null && !starting.current) {
      starting.current = true;
      void start({ learnerKey });
    }
  }, [state, learnerKey, start]);

  useEffect(() => {
    setRaw("");
    setUnparsable(false);
  }, [question?.itemKey]);

  const submit = (idk: boolean): void => {
    if (busy || question === null) return;
    if (!idk && raw.trim().length === 0) return;
    setBusy(true);
    setUnparsable(false);
    void answer({ learnerKey, itemKey: question.itemKey, raw: idk ? "" : raw })
      .then((result) => {
        setBusy(false);
        if (result.verdict === "unparsable") {
          setUnparsable(true);
          return;
        }
        setLast(result);
        if (result.finished) setFinished(true);
      })
      .catch((error: unknown) => {
        setBusy(false);
        console.error(error);
      });
  };

  if (state === undefined || (state === null && starting.current)) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 180 }} />
        <div className="card">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  if (finished || state?.status === "done") {
    return (
      <div className="cols cols--solo">
        <div className="col">
          <div className="eyebrow">Диагностика</div>
          <h1 className="h1">Карта построена</h1>
          <p className="lede">
            Вопросов задано <span className="num">{state?.askedCount ?? 0}</span> из{" "}
            <span className="num">{state?.budget ?? 15}</span>. Дальше — набор дня.
          </p>
          <div className="row row--actions" style={{ marginTop: 16 }}>
            <button className="btn btn--primary btn--big" onClick={onFinished}>
              К набору
            </button>
          </div>
        </div>
      </div>
    );
  }

  const asked = state?.askedCount ?? 0;
  const budget = state?.budget ?? 15;

  return (
    <div className="cols">
      <div className="col">
        <div>
          <div className="eyebrow">Диагностика</div>
          <h1 className="h1">
            Вопрос <span className="num">{asked + 1}</span> из{" "}
            <span className="num">{budget}</span>
          </h1>
          <p className="lede">
            Вопросы не по порядку и не по темам: каждый выбирается по тому, что
            уже сказали предыдущие.
          </p>
        </div>

        <div className="card">
          <div className="card__label">Условие</div>
          {question ? (
            <div className="statement">
              <Formula source={question.statement} chip />
            </div>
          ) : (
            <div className="skeleton" style={{ marginTop: 14, height: 26 }} />
          )}
        </div>

        <div className="card">
          <div className="answer">
            <span className="answer__label">Ответ</span>
            <input
              className={`field${unparsable ? " field--bad" : ""}`}
              value={raw}
              autoFocus
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              placeholder={question?.inputHint ?? ""}
              onChange={(event) => setRaw(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") submit(false);
              }}
            />
            <button
              className="btn btn--primary"
              disabled={busy || raw.trim().length === 0 || !question}
              onClick={() => submit(false)}
            >
              {busy ? "Проверяю" : "Ответить"}
            </button>
            <button
              className="btn btn--outline"
              disabled={busy || !question}
              onClick={() => submit(true)}
            >
              Не знаю
            </button>
          </div>

          {unparsable ? (
            <div className="notice" style={{ marginTop: 12 }}>
              Не разобрано. Поле принимает {question?.inputHint}. Вопрос остался
              тот же — на оценку это не повлияло.
            </div>
          ) : null}
        </div>
      </div>

      <div className="col">
        {last ? (
          <div className="card">
            <div className="card__label">Предыдущий вопрос</div>
            <div
              className={`banner ${last.verdict === "correct" ? "banner--grow" : "banner--miss"}`}
              style={{ marginTop: 12 }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="banner__title">
                  {last.verdict === "correct" ? "Сходится" : "Не сходится"}
                </div>
                {last.answerDisp !== null ? (
                  <div className="banner__line">
                    Верно{" "}
                    <span className="underline--grow">
                      <Formula source={last.answerDisp} />
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
            {/* Разбор приходит и на верном ответе: диагностика тоже учит. */}
            <Solution steps={last.solution} />
          </div>
        ) : null}

        <div className="card card--dashed">
          <div className="card__label">Зачем это</div>
          <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            Пятнадцать вопросов вместо ста шестидесяти четырёх: карта навыков
            знает, что из чего следует, и один ответ говорит о десятках тем
            сразу. «Не знаю» здесь честнее угадывания — оно экономит вопросы
            сильнее всего остального.
          </div>
        </div>
      </div>
    </div>
  );
}
